import { useEffect, useRef, useState } from 'react'
import type { Candle, Timeframe } from '@/types'

// Binance USD-M Futures WebSocket market streams.
//
// Kline/candlestick is a "market" stream, which lives under the routed /market
// endpoint. The legacy unrouted base (wss://fstream.binance.com/ws) was removed
// in the Binance WebSocket system upgrade (legacy shutdown 2026-04-23): after
// the upgrade, unrouted connections only receive /public data, so /market
// streams like kline connect but never push any message. Must use /market/ws.
// Docs: https://developers.binance.com/docs/derivatives/usds-margined-futures/websocket-market-streams/Important-WebSocket-Change-Notice
const BINANCE_FUTURES_WS = 'wss://fstream.binance.com/market/ws'

const RECONNECT_DELAY_MS = 3000

const INTERVAL_MAP: Record<Timeframe, string> = {
  '1m': '1m',
  '5m': '5m',
  '15m': '15m',
  '1h': '1h',
  '4h': '4h',
  '1d': '1d',
}

interface BinanceKlineEvent {
  e: string
  E: number
  s: string
  k: {
    t: number    // Kline start time
    T: number    // Kline close time
    s: string
    i: string
    o: string    // Open
    c: string    // Close
    h: string    // High
    l: string    // Low
    v: string    // Volume
    n: number    // Number of trades
    x: boolean   // Is kline closed?
  }
}

interface UseBinanceKlineOptions {
  symbol: string
  timeframe: Timeframe
  enabled?: boolean
}

export function useBinanceKline({
  symbol,
  timeframe,
  enabled = true,
}: UseBinanceKlineOptions): Candle | null {
  const [candle, setCandle] = useState<Candle | null>(null)
  const wsRef = useRef<WebSocket | null>(null)
  const reconnectTimeoutRef = useRef<ReturnType<typeof setTimeout>>()

  useEffect(() => {
    if (!enabled) return

    setCandle(null)

    // `stopped` is scoped to this effect run. It guards against the previous
    // reconnect bug: closing a socket (on cleanup or when replacing it) fires
    // `onclose` asynchronously, which used to schedule a fresh reconnect that
    // outlived the effect. On a timeframe switch this spawned competing
    // reconnect chains that repeatedly closed each other's sockets, so no
    // socket ever lived long enough to stream data.
    let stopped = false

    const openSocket = () => {
      if (stopped) return

      const interval = INTERVAL_MAP[timeframe]
      const stream = `${symbol.toLowerCase()}@kline_${interval}`
      const ws = new WebSocket(`${BINANCE_FUTURES_WS}/${stream}`)
      wsRef.current = ws

      ws.onopen = () => {
        console.log(`Binance kline WS connected: ${stream}`)
      }

      ws.onmessage = (event) => {
        try {
          const data: BinanceKlineEvent = JSON.parse(event.data)
          if (data.e === 'kline') {
            setCandle({
              time: Math.floor(data.k.t / 1000),
              open: parseFloat(data.k.o),
              high: parseFloat(data.k.h),
              low: parseFloat(data.k.l),
              close: parseFloat(data.k.c),
              volume: parseFloat(data.k.v),
            })
          }
        } catch (error) {
          console.error('Failed to parse Binance kline:', error)
        }
      }

      ws.onclose = () => {
        // Ignore intentional closes (cleanup / socket replacement).
        if (stopped) return
        console.log('Binance kline WS disconnected, reconnecting...')
        reconnectTimeoutRef.current = setTimeout(openSocket, RECONNECT_DELAY_MS)
      }

      ws.onerror = (error) => {
        // An error is always followed by `onclose`, which handles reconnect.
        console.error('Binance kline WS error:', error)
      }
    }

    openSocket()

    return () => {
      stopped = true
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current)
      }
      if (wsRef.current) {
        // Detach onclose so the intentional close cannot schedule a reconnect.
        wsRef.current.onclose = null
        wsRef.current.close()
        wsRef.current = null
      }
    }
  }, [symbol, timeframe, enabled])

  return candle
}
